import { Trans, useLingui } from '@lingui/react/macro';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { addMinutes } from 'date-fns/addMinutes';
import { formatDateTime } from '@/common/utils/formatDateTime.ts';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export interface EventTimeProps {
  eventAt: Date;
  eventUtcOffset: number;
  horizontalAlign?: 'start' | 'end';
}

export const EventTime = ({
  eventAt,
  eventUtcOffset,
  horizontalAlign = 'start',
}: EventTimeProps) => {
  const { t } = useLingui();

  const userTzOffset = -eventAt.getTimezoneOffset() / 60;
  const isInSameOffset = userTzOffset === eventUtcOffset;
  const offsetFromUser = eventUtcOffset - userTzOffset;

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent={horizontalAlign}
        gap={1}
        py={isInSameOffset ? 1 : 0}
        alignItems="center"
      >
        <Typography color="textSecondary" lineHeight={1}>
          {isInSameOffset ? (
            <Tooltip title={<Trans>Your and dispenser local time is the same</Trans>}>
              <AccessTimeIcon fontSize="small" />
            </Tooltip>
          ) : (
            <Tooltip title={<Trans>Dispenser local time</Trans>}>
              <PlaceOutlinedIcon fontSize="small" />
            </Tooltip>
          )}
        </Typography>
        <Typography aria-description={t`Dispenser local time`} data-testid="event-local-time">
          {formatDateTime(addMinutes(eventAt, offsetFromUser * 60))}
        </Typography>
      </Stack>
      {isInSameOffset ? null : (
        <Stack direction="row" justifyContent={horizontalAlign} gap={1} alignItems="center">
          <Typography color="textSecondary" lineHeight={1}>
            <Tooltip title={<Trans>Your local time</Trans>}>
              <PersonOutlinedIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Typography variant="body2" color="textSecondary" aria-description={t`Your local time`}>
            {formatDateTime(eventAt)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
